import { useState } from "react";
import { CAREERS_TRACKS, LIFESTYLE } from "@/data/products";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function Careers() {
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", role: "", experience_years: 0, cover_letter: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/careers/apply", form);
      toast.success(data.message);
      setDone(true);
    } catch { toast.error("Please try again."); }
    setLoading(false);
  };

  return (
    <div data-testid="careers-page">
      <section className="relative pt-20 pb-24 bg-neutral-950 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img src={LIFESTYLE.team} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-neutral-950/40 to-neutral-950" />
        <div className="relative max-w-5xl mx-auto px-6">
          <div className="eyebrow text-gold-400 mb-4">Careers</div>
          <h1 className="font-display font-bold text-5xl lg:text-7xl tracking-tighter leading-[0.95]">Build the next generation of <span className="gold-gradient-text">global beverages.</span></h1>
          <p className="mt-6 max-w-2xl text-neutral-300">Join a fast-growing British beverage company with international reach. Meaningful work, real ownership, world-class colleagues.</p>
        </div>
      </section>

      <section className="py-24 bg-background">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="eyebrow">Where you can grow</div>
            <h2 className="font-display font-bold text-4xl tracking-tighter mt-2">Career tracks</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {CAREERS_TRACKS.map((t, i) => (
              <div key={t.title} className="p-6 rounded-2xl border border-border hover:border-gold-400 transition-colors">
                <div className="text-[10px] uppercase tracking-widest text-gold-500 font-bold">Track 0{i+1}</div>
                <div className="font-display font-bold text-xl mt-2">{t.title}</div>
                <p className="text-sm text-muted-foreground mt-2">{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-muted/40">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-10">
            <div className="eyebrow">Speculative Application</div>
            <h2 className="font-display font-bold text-4xl tracking-tighter mt-2">Introduce yourself to SBC</h2>
          </div>
          {done ? (
            <div className="text-center p-10 rounded-2xl bg-background border border-border">
              <div className="w-14 h-14 mx-auto rounded-full bg-gold-400 text-neutral-950 flex items-center justify-center font-black text-xl">✓</div>
              <p className="mt-4 font-display text-xl">Thank you &mdash; we&rsquo;ve received your application.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-background rounded-2xl border border-border p-6 space-y-4" data-testid="career-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <Input label="Full name *" value={form.full_name} onChange={(v) => setForm({ ...form, full_name: v })} required testId="career-name" />
                <Input label="Email *" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required testId="career-email" />
                <Input label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testId="career-phone" />
                <Input label="Role of interest *" value={form.role} onChange={(v) => setForm({ ...form, role: v })} required testId="career-role" />
              </div>
              <div>
                <Label>Years of experience</Label>
                <input type="number" min="0" value={form.experience_years} onChange={(e) => setForm({ ...form, experience_years: parseInt(e.target.value || 0) })} className="w-full h-11 rounded-lg border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold-400" data-testid="career-experience" />
              </div>
              <div>
                <Label>Cover Letter</Label>
                <textarea rows={5} value={form.cover_letter} onChange={(e) => setForm({ ...form, cover_letter: e.target.value })} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold-400" data-testid="career-cover" />
              </div>
              <button disabled={loading} className="w-full bg-neutral-950 dark:bg-white dark:text-neutral-950 text-white rounded-full h-12 font-semibold flex items-center justify-center gap-2 disabled:opacity-50" data-testid="career-submit">
                {loading && <Loader2 className="w-4 h-4 animate-spin" />} Submit Application
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

const Label = ({ children }) => <div className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground mb-2">{children}</div>;
const Input = ({ label, value, onChange, required, type = "text", testId }) => (
  <div>
    <Label>{label}</Label>
    <input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)} className="w-full h-11 rounded-lg border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold-400" data-testid={testId} />
  </div>
);
